import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { healthQuotes } from '../utils/quotes';
import { useLanguage } from '../context/LanguageContext';
import { useRadio } from '../context/RadioContext';

const { FiX, FiActivity, FiTrendingUp, FiTarget, FiClock, FiPlus, FiMinus, FiPlay, FiPause, FiMusic, FiRadio, FiMapPin } = FiIcons;

const WorkoutOverlay = ({ isVisible, onClose, timerData }) => {
  const { t } = useLanguage();
  const { isPlaying, togglePlay } = useRadio();
  const [currentQuote, setCurrentQuote] = useState('');
  const [showMusicPrompt, setShowMusicPrompt] = useState(false);

  useEffect(() => {
    if (isVisible && healthQuotes && healthQuotes.length > 0) {
      setCurrentQuote(healthQuotes[Math.floor(Math.random() * healthQuotes.length)]);
      const interval = setInterval(() => {
        const randomIndex = Math.floor(Math.random() * healthQuotes.length);
        setCurrentQuote(healthQuotes[randomIndex]);
      }, 30000);

      return () => clearInterval(interval);
    }
  }, [isVisible]);

  // Show music prompt when overlay opens if music is not playing
  useEffect(() => {
    if (isVisible && !isPlaying) {
      const timer = setTimeout(() => {
        setShowMusicPrompt(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [isVisible]);

  if (!isVisible || !timerData) return null;

  const { timeRemaining, currentPhase, progressPercentage, handlePause, isPaused, currentSpeed, adjustSpeed, distanceTraveled } = timerData;

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatDistance = (meters) => {
    if (meters >= 1000) {
      return `${(meters / 1000).toFixed(2)} km`;
    }
    return `${Math.floor(meters)} m`;
  };

  const getPhaseColor = (name) => {
    switch (name) {
      case 'warmup': return 'from-amber-500 to-orange-600';
      case 'intense': return 'from-rose-500 to-pink-600';
      case 'cooldown': return 'from-blue-500 to-cyan-600';
      default: return 'from-slate-700 to-slate-800';
    }
  };

  const phaseLabel = currentPhase?.name === 'warmup' ? t('warmup') : currentPhase?.name === 'intense' ? t('intense') : currentPhase?.name === 'cooldown' ? t('cooldown') : '';

  const handleMusicYes = () => {
    togglePlay();
    setShowMusicPrompt(false);
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-slate-900 text-white flex flex-col"
    >
      {/* Dynamic Background */}
      <div className={`absolute inset-0 bg-gradient-to-br ${getPhaseColor(currentPhase?.name)} opacity-20 transition-all duration-1000`} />
      <div className="absolute inset-0 backdrop-blur-3xl" />

      {/* Music Prompt Modal */}
      <AnimatePresence>
        {showMusicPrompt && (
          <motion.div 
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -50 }}
            className="absolute top-24 left-0 right-0 z-50 flex justify-center pointer-events-none"
          >
            <div className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl p-4 shadow-2xl pointer-events-auto flex items-center gap-4 max-w-sm mx-4">
              <div className="p-3 bg-rose-500 rounded-full text-white">
                <SafeIcon icon={FiMusic} />
              </div>
              <div className="flex-1">
                <p className="font-bold text-sm mb-2">{t('want_music')}</p>
                <div className="flex gap-2">
                  <button 
                    onClick={handleMusicYes}
                    className="px-3 py-1.5 bg-white text-slate-900 rounded-lg text-xs font-bold hover:bg-slate-100 transition-colors"
                  >
                    {t('yes')}
                  </button>
                  <button 
                    onClick={() => setShowMusicPrompt(false)}
                    className="px-3 py-1.5 bg-white/10 text-white rounded-lg text-xs font-bold hover:bg-white/20 transition-colors"
                  >
                    {t('no')}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header */}
      <div className="relative z-10 flex justify-between items-center p-6 md:p-8">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-3 px-4 py-2 bg-white/10 rounded-full backdrop-blur-md border border-white/10">
            <SafeIcon icon={FiActivity} className="text-white/90" />
            <span className="font-bold text-sm tracking-wide uppercase">{phaseLabel}</span>
          </div>
          
          {/* Radio Button in Header */}
          <button 
            onClick={togglePlay}
            className={`p-2.5 rounded-full transition-all backdrop-blur-md border ${isPlaying ? 'bg-rose-500 text-white border-rose-400' : 'bg-white/10 text-white/70 border-white/10 hover:bg-white/20'}`}
          >
            <SafeIcon icon={isPlaying ? FiRadio : FiMusic} className={isPlaying ? "animate-pulse" : ""} />
          </button>
        </div>

        <button 
          onClick={onClose}
          className="p-3 bg-white/10 hover:bg-white/20 rounded-full transition-colors backdrop-blur-md border border-white/10"
        >
          <SafeIcon icon={FiX} className="text-xl" />
        </button>
      </div>

      {/* Main Content */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center p-6 w-full max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row items-center justify-center gap-8 lg:gap-16 w-full">
          
          {/* Left: Controls */}
          <div className="flex-1 flex flex-col items-center max-w-md w-full">
            <motion.div 
              key={timeRemaining}
              className="text-[100px] md:text-[160px] font-bold tracking-tighter leading-none font-variant-numeric tabular-nums bg-clip-text text-transparent bg-gradient-to-b from-white to-white/50"
            >
              {formatTime(timeRemaining)}
            </motion.div>
            
            <div className="mt-8 md:mt-12 grid grid-cols-2 gap-4 w-full">
              {/* Speed Control */}
              <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-6 border border-white/10">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-xs font-bold text-white/60 uppercase tracking-wider">{t('speed')}</span>
                  <span className="text-xs font-bold text-white/60 uppercase tracking-wider">KM/H</span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <button onClick={() => adjustSpeed(-0.1)} className="p-3 bg-white/10 hover:bg-white/20 rounded-xl transition-all active:scale-95">
                    <SafeIcon icon={FiMinus} className="text-xl" />
                  </button>
                  <span className="text-4xl font-bold tabular-nums">{currentSpeed.toFixed(1)}</span>
                  <button onClick={() => adjustSpeed(0.1)} className="p-3 bg-white/10 hover:bg-white/20 rounded-xl transition-all active:scale-95">
                    <SafeIcon icon={FiPlus} className="text-xl" />
                  </button>
                </div>
              </div>

              {/* Distance Display */}
              <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-6 border border-white/10 flex flex-col justify-between">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs font-bold text-white/60 uppercase tracking-wider">{t('distance')}</span>
                  <SafeIcon icon={FiMapPin} className="text-white/50" />
                </div>
                <div className="text-center flex-1 flex items-center justify-center">
                  <span className="text-3xl font-bold tabular-nums">{formatDistance(distanceTraveled)}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Motivation */}
          <div className="flex-1 hidden lg:block max-w-xl">
            <AnimatePresence mode="wait">
              <motion.div 
                key={currentQuote}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="border-l-4 border-white/30 pl-8 py-4"
              >
                 <p className="text-sm font-bold text-white/70 uppercase tracking-wider mb-4 flex items-center gap-2">
                   <SafeIcon icon={FiTrendingUp} />
                   {t('did_you_know')}
                 </p>
                <p className="text-3xl font-light italic leading-relaxed text-white/90">
                  "{currentQuote}"
                </p>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="relative z-10 p-8 md:p-12">
        <div className="max-w-3xl mx-auto w-full">
          <div className="flex items-center gap-6">
            <button 
              onClick={handlePause}
              className={`px-8 py-4 rounded-2xl font-bold flex items-center gap-3 transition-all active:scale-95 ${isPaused ? 'bg-white text-slate-900' : 'bg-white/20 hover:bg-white/30 text-white'}`}
            >
              <SafeIcon icon={isPaused ? FiPlay : FiPause} className="text-xl" />
              {isPaused ? t('continue') : t('pause')}
            </button>
            
            <div className="flex-1">
              <div className="flex justify-between text-xs font-bold text-white/60 mb-2 uppercase tracking-wider">
                <span>{t('progress')}</span>
                <span>{Math.round(progressPercentage)}%</span>
              </div>
              <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                <motion.div 
                  className="h-full bg-white shadow-[0_0_20px_rgba(255,255,255,0.5)]"
                  animate={{ width: `${progressPercentage}%` }}
                  transition={{ duration: 0.5 }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default WorkoutOverlay;